/**
 * Conversation AI Service for Discovery Assistant
 *
 * Handles AI-powered conversation flow, answer quality assessment,
 * follow-up generation, and live validation feedback.
 */

import { callClaude, callClaudeStreaming, parseJsonResponse, type ClaudeConfig } from './claude'

// Question Framework - 12 questions across 4 phases
export interface QuestionConfig {
  number: number
  question: string
  phase: 'market_reality' | 'competitive_landscape' | 'value_design' | 'business_viability'
  primaryEntity: string | null
  facetTypes: string[]
  qualitySignals: string[]
  followUps: Record<string, string>
}

export const DISCOVERY_QUESTIONS: QuestionConfig[] = [
  {
    number: 1,
    question: "Who specifically has this problem?",
    phase: 'market_reality',
    primaryEntity: 'CustomerSegment',
    facetTypes: ['persona', 'market_segment', 'stakeholder_need'],
    qualitySignals: ['specific_role', 'observable_behavior', 'clear_outcome'],
    followUps: {
      vague: "Which kind specifically? What are they doing when they hit this problem?",
      multiple: "Which ONE would you start with? Who has the problem most acutely?",
      no_behavior: "What are they actually doing when they encounter this problem?",
    }
  },
  {
    number: 2,
    question: "What outcome are they trying to achieve?",
    phase: 'market_reality',
    primaryEntity: 'CustomerJob',
    facetTypes: ['customer_job', 'success_criteria', 'outcome_requirement'],
    qualitySignals: ['outcome_not_method', 'success_criteria', 'job_importance'],
    followUps: {
      method_not_outcome: "That's how. But why? What's the outcome they're after?",
      too_abstract: "Time on what specifically? And what would they do with that time?",
      solution_bias: "Forget your product. What were they trying to do BEFORE you existed?",
    }
  },
  {
    number: 3,
    question: "What's blocking them from achieving that outcome?",
    phase: 'market_reality',
    primaryEntity: 'Problem',
    facetTypes: ['problem', 'blocker', 'root_cause', 'pain_trigger'],
    qualitySignals: ['root_cause', 'specific', 'connected_to_frustration'],
    followUps: {
      symptom: "Why does that happen? What's the underlying cause?",
      multiple: "Which is the biggest? If you could only solve one?",
      vague: "Can you give me an example of when this happened to a real customer?",
    }
  },
  {
    number: 4,
    question: "What do they actually do today to deal with this?",
    phase: 'competitive_landscape',
    primaryEntity: 'Alternative',
    facetTypes: ['competitor', 'competitive_advantage', 'market_gap', 'workaround'],
    qualitySignals: ['multiple_alternatives', 'includes_do_nothing', 'specific_names'],
    followUps: {
      only_competitors: "What about manual workarounds? Spreadsheets? Just tolerating it?",
      nothing: "Really nothing? How do they cope when this problem hits?",
      generic: "Which tools specifically? Give me names.",
    }
  },
  {
    number: 5,
    question: "What's wrong with those alternatives?",
    phase: 'competitive_landscape',
    primaryEntity: 'AlternativeDisadvantage',
    facetTypes: ['competitive_gap', 'limitation', 'user_frustration'],
    qualitySignals: ['specific_flaws', 'quantified', 'opportunity_space'],
    followUps: {
      vague: "Not great how? What specifically goes wrong?",
      no_numbers: "Can you quantify that? How much time? Money? Error rate?",
      assumed: "How do you know this? Have customers told you?",
    }
  },
  {
    number: 6,
    question: "What does living with these flaws cost them?",
    phase: 'competitive_landscape',
    primaryEntity: 'CustomerPain',
    facetTypes: ['customer_pain', 'emotional_impact', 'cost_impact', 'urgency_driver'],
    qualitySignals: ['quantified_impact', 'emotional_dimension', 'urgency_identified'],
    followUps: {
      no_numbers: "How much specifically? Per week? Per month?",
      only_functional: "What about stress? Career impact? Reputation?",
      low_consequence: "If the cost is low, why would they switch?",
    }
  },
  {
    number: 7,
    question: "What do you offer?",
    phase: 'value_design',
    primaryEntity: 'ValueProposition',
    facetTypes: ['value_proposition', 'unique_capability', 'core_mechanism'],
    qualitySignals: ['category_context', 'clear_mechanism', 'focused'],
    followUps: {
      feature_list: "Zoom out. What IS it fundamentally? What category?",
      no_mechanism: "HOW does it work? What's the engine that makes it possible?",
      buzzwords: "Pretend I've never heard of AI. Explain what it actually DOES.",
    }
  },
  {
    number: 8,
    question: "How does it remove the root problem?",
    phase: 'value_design',
    primaryEntity: null,
    facetTypes: ['solution_fit', 'before_after', 'problem_resolution'],
    qualitySignals: ['direct_link', 'before_after', 'quantified'],
    followUps: {
      no_connection: "You said the problem was X. How does your solution address X?",
      vague_improvement: "How much better? Give me numbers.",
      feature_not_benefit: "That's what it does. What does that MEAN for the customer?",
    }
  },
  {
    number: 9,
    question: "How is it better than their current alternatives?",
    phase: 'value_design',
    primaryEntity: null,
    facetTypes: ['differentiation', 'competitive_advantage', 'unique_value'],
    qualitySignals: ['head_to_head', 'relative_advantage', 'addresses_flaws'],
    followUps: {
      generic: "Better how specifically? On which dimension?",
      no_connection: "You said [competitor] has problem X. How do you solve that?",
      same_as_competitor: "Doesn't [competitor] claim the same? What's actually different?",
    }
  },
  {
    number: 10,
    question: "Will they pay for it?",
    phase: 'business_viability',
    primaryEntity: 'RevenueStream',
    facetTypes: ['pricing', 'willingness_to_pay', 'revenue_model', 'price_evidence'],
    qualitySignals: ['evidence_not_assumption', 'reference_price', 'tested'],
    followUps: {
      verbal_only: "Did you actually ask for money? What happened?",
      no_evidence: "What are they paying for alternatives today?",
      single_price: "Did you test different prices? What did you learn?",
    }
  },
  {
    number: 11,
    question: "What would stop them from switching?",
    phase: 'business_viability',
    primaryEntity: null,
    facetTypes: ['adoption_barrier', 'risk', 'switching_cost', 'buyer_concern'],
    qualitySignals: ['anxiety_acknowledged', 'habit_acknowledged', 'plan_exists'],
    followUps: {
      none: "What about integration? Learning curve? Convincing their boss?",
      technical_only: "What about political blockers? Who else needs to approve?",
      no_habit: "What's comfortable about their current way, even if worse?",
    }
  },
  {
    number: 12,
    question: "Why would they switch NOW?",
    phase: 'business_viability',
    primaryEntity: 'MarketTrend',
    facetTypes: ['market_trend', 'timing_trigger', 'urgency_driver', 'buying_signal'],
    qualitySignals: ['specific_trigger', 'connected_to_timing', 'detectable'],
    followUps: {
      whenever: "But what CAUSES them to need it? What event triggers the search?",
      frustration_only: "Is frustration enough? Or do they need a push?",
      no_urgency: "If no urgency, why prioritize this over other things?",
    }
  }
]

// Quality levels
export type AnswerQuality = 'shallow' | 'assumed' | 'anecdotal' | 'validated' | 'bulletproof'

// Labels are designed to be encouraging rather than judgmental
// Colors progress from neutral to positive (no harsh reds)
export const QUALITY_CONFIG: Record<AnswerQuality, { label: string; color: string; confidence: number }> = {
  shallow: { label: 'Starting', color: '#94a3b8', confidence: 20 }, // slate-400, neutral
  assumed: { label: 'Building', color: '#64748b', confidence: 40 }, // slate-500, progressing
  anecdotal: { label: 'Story-Based', color: '#f59e0b', confidence: 60 }, // amber-500, warm
  validated: { label: 'Validated', color: '#22c55e', confidence: 80 }, // green-500
  bulletproof: { label: 'Evidence-Based', color: '#10b981', confidence: 100 }, // emerald-500
}

// Phase metadata
export const PHASE_CONFIG = {
  market_reality: { label: 'Market Reality', color: '#22c55e', questions: [1, 2, 3] },
  competitive_landscape: { label: 'Competitive Landscape', color: '#3b82f6', questions: [4, 5, 6] },
  value_design: { label: 'Value Design', color: '#f59e0b', questions: [7, 8, 9] },
  business_viability: { label: 'Business Viability', color: '#8b5cf6', questions: [10, 11, 12] },
}

// Assessment result
export interface QualityAssessment {
  score: AnswerQuality
  confidence: number
  issues: string[]
  suggestions: string[]
  followUpKey?: string
}

// Suggestion from AI
export interface AISuggestion {
  text: string
  type: 'completion' | 'improvement' | 'alternative'
  confidence: number
}

/**
 * INSTANT client-side quality assessment - no AI, runs synchronously
 * Use this for immediate feedback as user types
 */
export function instantAssessment(
  answer: string,
  questionNumber: number
): QualityAssessment {
  const question = DISCOVERY_QUESTIONS[questionNumber - 1]
  if (!question) {
    return { score: 'shallow', confidence: 0, issues: ['Invalid question'], suggestions: [] }
  }

  const words = answer.split(/\s+/).filter(w => w.length > 0)
  const wordCount = words.length
  const lowerAnswer = answer.toLowerCase()

  // Signal detection patterns - be PERMISSIVE, not restrictive
  const hasNumbers = /\d+/.test(answer)
  // Personas: B2B roles OR B2C consumer types
  const hasSpecificRole = /manager|director|ceo|founder|engineer|specialist|lead|head of|vp |cto|cfo|analyst|designer|developer|architect|consultant|owner|owners|homeowner|parent|student|teacher|freelancer|entrepreneur|traveler|shopper|buyer|professional|executive|admin|coordinator|assistant/i.test(answer)
  // Industries & verticals - broad matching
  const hasIndustry = /b2b|b2c|saas|fintech|healthcare|ecommerce|e-commerce|retail|agency|agencies|marketing|sales|hr |finance|legal|real estate|education|logistics|interior|design|home|restaurant|travel|fitness|wellness|tech|software|construction|manufacturing|consulting|media|entertainment|hospitality|automotive/i.test(answer)
  // Geographic or demographic context
  const hasGeoOrDemo = /belgium|netherlands|europe|usa|america|uk|germany|france|spain|italy|asia|africa|urban|rural|city|local|region|country|young|old|senior|millennial|aged|living in|based in/i.test(answer)
  const hasCompanySize = /startup|enterprise|smb|small|medium|large|fortune|500|employee|team|company|companies|organization/i.test(answer)
  const hasEvidence = /example|instance|customer|they said|told me|interview|survey|data|research|talked to|spoke with|observed/i.test(answer)
  const hasQuantification = /\d+%|\d+ (customer|user|company|people|team)|most|majority|all of|none of|every|many|few/i.test(answer)
  const hasSpecificTool = /excel|spreadsheet|slack|notion|jira|salesforce|hubspot|google|microsoft|asana|trello|monday|confluence|figma/i.test(answer)
  // Context: what they want/need/do - very permissive
  const hasContext = /who need|that need|need |trying to|want |wants|wanting|looking for|struggling|dealing with|facing|experiencing|managing|working on|responsible for|interested in|searching|decoration|decorating|improve|improving|buying|purchasing|renovating/i.test(answer)

  // Score calculation
  let score: AnswerQuality = 'shallow'
  const issues: string[] = []
  const suggestions: string[] = []

  // Minimum length check
  if (wordCount < 4) {
    issues.push('Too brief')
    suggestions.push('Be more specific about who')
    return { score: 'shallow', confidence: 10, issues, suggestions }
  }

  // Count quality signals
  let signalCount = 0
  if (hasNumbers) signalCount++
  if (hasSpecificRole) signalCount++
  if (hasIndustry) signalCount++
  if (hasGeoOrDemo) signalCount++
  if (hasCompanySize) signalCount++
  if (hasEvidence) signalCount++
  if (hasQuantification) signalCount++
  if (hasSpecificTool) signalCount++
  if (hasContext) signalCount++

  // Q1-specific: Target customer - be PERMISSIVE
  // A good answer has: WHO (role/persona) + CONTEXT (what they want/need OR where they are)
  if (questionNumber === 1) {
    const hasWho = hasSpecificRole
    const hasWhat = hasIndustry || hasContext
    const hasWhere = hasGeoOrDemo

    if (hasWho && (hasWhat || hasWhere) && wordCount >= 6) {
      score = 'anecdotal' // Good target customer description
      if (hasEvidence || hasQuantification) {
        score = 'validated'
      }
    } else if (hasWho && wordCount >= 5) {
      score = 'assumed' // Has persona, just needs more context
    } else if (signalCount >= 1 && wordCount >= 4) {
      score = 'assumed' // Has something useful
    }
  } else {
    // Generic scoring for other questions
    if (signalCount >= 4 && wordCount > 30 && hasQuantification) {
      score = 'bulletproof'
    } else if (signalCount >= 3 && wordCount > 20 && hasEvidence) {
      score = 'validated'
    } else if (signalCount >= 2 && wordCount > 12) {
      score = 'anecdotal'
    } else if (signalCount >= 1 && wordCount > 8) {
      score = 'assumed'
    } else if (wordCount > 6) {
      score = 'assumed'
    }
  }

  // Generate contextual suggestions
  if (questionNumber === 1) {
    if (!hasSpecificRole) suggestions.push('Add a specific role (e.g., product managers)')
    if (!hasIndustry && !hasCompanySize) suggestions.push('Add industry or company type')
    if (!hasContext && score !== 'validated') suggestions.push('Add what they\'re trying to do')
  } else {
    if (!hasEvidence && score !== 'bulletproof') suggestions.push('Add evidence or examples')
    if (!hasNumbers && !hasQuantification) suggestions.push('Add specifics or numbers')
  }

  // Issues for feedback
  if (score === 'shallow') {
    issues.push('Need more specifics')
  }

  // Use lowerAnswer for debugging if needed
  void lowerAnswer

  return {
    score,
    confidence: QUALITY_CONFIG[score].confidence,
    issues,
    suggestions: suggestions.slice(0, 2),
  }
}

/**
 * Assess the quality of an answer for a specific question (AI-powered, async)
 * Use for deeper analysis after user pauses typing
 */
export async function assessAnswerQuality(
  config: ClaudeConfig,
  answer: string,
  questionNumber: number,
  previousAnswers?: Record<number, string>
): Promise<QualityAssessment> {
  const question = DISCOVERY_QUESTIONS[questionNumber - 1]
  if (!question) {
    return { score: 'shallow', confidence: 0, issues: ['Invalid question'], suggestions: [] }
  }

  const systemPrompt = `You are a rigorous business analyst assessing the quality of answers in a PMF discovery interview.

Assess this answer to "${question.question}" (Question ${questionNumber}).

Quality signals to look for:
${question.qualitySignals.map(s => `- ${s}`).join('\n')}

Quality levels:
- shallow: Vague, generic, no specifics
- assumed: Logical but unvalidated assumptions
- anecdotal: Based on 1-2 examples, not pattern
- validated: Multiple evidence points, specific details
- bulletproof: Quantified, stress-tested, multiple sources

Respond in JSON:
{
  "score": "shallow" | "assumed" | "anecdotal" | "validated" | "bulletproof",
  "confidence": 0-100,
  "issues": ["issue1", "issue2"],
  "suggestions": ["suggestion1", "suggestion2"],
  "followUpKey": "key from followUps if applicable"
}`

  const contextStr = previousAnswers
    ? `\n\nPrevious context:\n${Object.entries(previousAnswers).map(([q, a]) => `Q${q}: ${a}`).join('\n')}`
    : ''

  const userMessage = `Answer to assess: "${answer}"${contextStr}`

  try {
    const response = await callClaude(config, systemPrompt, userMessage)
    const result = parseJsonResponse<QualityAssessment>(response)
    if (result) return result
  } catch (error) {
    console.error('[conversationAI] Assessment error:', error)
  }

  // Fallback: basic heuristic assessment
  const wordCount = answer.split(/\s+/).length
  let score: AnswerQuality = 'shallow'

  if (wordCount > 50 && /\d/.test(answer)) score = 'validated'
  else if (wordCount > 30 && /example|instance|customer|they said/i.test(answer)) score = 'anecdotal'
  else if (wordCount > 20 && /because|therefore|specifically/i.test(answer)) score = 'assumed'
  else if (wordCount > 10) score = 'assumed'

  return {
    score,
    confidence: QUALITY_CONFIG[score].confidence,
    issues: score === 'shallow' ? ['Answer lacks specificity'] : [],
    suggestions: ['Add specific examples or data'],
  }
}

/**
 * Get AI suggestions to improve/complete an answer
 */
export async function getSuggestions(
  config: ClaudeConfig,
  partialAnswer: string,
  questionNumber: number,
  previousAnswers?: Record<number, string>
): Promise<AISuggestion[]> {
  const question = DISCOVERY_QUESTIONS[questionNumber - 1]
  if (!question) return []

  const systemPrompt = `You are a helpful business advisor providing suggestions to improve PMF discovery answers.

Question: "${question.question}"

Provide 3 suggestions based on the partial answer:
1. A completion that finishes their thought
2. An improvement that adds specificity or evidence
3. An alternative framing that might be stronger

Format: JSON array of {text, type, confidence}
Types: "completion" | "improvement" | "alternative"
Keep suggestions concise (1-2 sentences each).`

  const contextStr = previousAnswers
    ? `\n\nContext from previous answers:\n${Object.entries(previousAnswers).map(([q, a]) => `Q${q}: ${a.substring(0, 100)}...`).join('\n')}`
    : ''

  const userMessage = `Partial answer: "${partialAnswer}"${contextStr}`

  try {
    const response = await callClaude(config, systemPrompt, userMessage)
    const result = parseJsonResponse<AISuggestion[]>(response)
    if (result && Array.isArray(result)) return result.slice(0, 3)
  } catch (error) {
    console.error('[conversationAI] Suggestions error:', error)
  }

  return []
}

/**
 * Generate the next assistant message in the conversation
 */
export async function generateAssistantResponse(
  config: ClaudeConfig,
  conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
  currentQuestionNumber: number,
  previousAnswers: Record<number, string>,
  onChunk?: (text: string) => void
): Promise<string> {
  const question = DISCOVERY_QUESTIONS[currentQuestionNumber - 1]
  if (!question) return "Let's wrap up your discovery session."

  const systemPrompt = `You are an expert PMF coach guiding a founder through discovery. Be conversational, warm but rigorous.

Current question (${currentQuestionNumber}/12): "${question.question}"
Phase: ${PHASE_CONFIG[question.phase].label}

Your job:
1. If the user just answered, acknowledge what they said briefly
2. Assess if the answer is complete/specific enough (look for: ${question.qualitySignals.join(', ')})
3. If incomplete, ask a follow-up to get more specificity
4. If complete, transition to the next question
5. If user asks off-topic questions, answer briefly then guide back

Keep responses concise (2-3 sentences). Be encouraging but push for specifics.
Don't say "great question" or excessive praise. Be direct like a good coach.`

  const contextMessages = conversationHistory.slice(-10).map(m =>
    `${m.role === 'user' ? 'Founder' : 'Coach'}: ${m.content}`
  ).join('\n\n')

  const answersContext = Object.entries(previousAnswers)
    .map(([q, a]) => `Q${q}: ${a.substring(0, 150)}${a.length > 150 ? '...' : ''}`)
    .join('\n')

  const userMessage = `Previous answers:\n${answersContext || '(none yet)'}\n\nConversation:\n${contextMessages}`

  if (onChunk) {
    return callClaudeStreaming(config, systemPrompt, userMessage, onChunk)
  }

  return callClaude(config, systemPrompt, userMessage)
}

/**
 * Check if user input is a question vs an answer
 */
export function isUserQuestion(text: string): boolean {
  const questionPatterns = [
    /^(what|why|how|when|where|who|which|can|could|would|should|is|are|do|does|did|will)\b/i,
    /\?$/,
    /^(tell me|explain|help me understand)/i,
  ]
  return questionPatterns.some(p => p.test(text.trim()))
}

/**
 * Determine if the current answer is good enough to proceed
 */
export function shouldProceedToNext(assessment: QualityAssessment): boolean {
  const passingScores: AnswerQuality[] = ['anecdotal', 'validated', 'bulletproof']
  return passingScores.includes(assessment.score)
}

/**
 * Get question by number
 */
export function getQuestion(number: number): QuestionConfig | undefined {
  return DISCOVERY_QUESTIONS[number - 1]
}

/**
 * Get phase for question number
 */
export function getPhaseForQuestion(number: number): typeof PHASE_CONFIG[keyof typeof PHASE_CONFIG] | undefined {
  const question = getQuestion(number)
  return question ? PHASE_CONFIG[question.phase] : undefined
}

/**
 * Parse poll data from message content
 * Handles multiple formats:
 * - Standard: ```json:poll\n{...}\n```
 * - Without newlines: ```json:poll{...}```
 * - Two backticks: ``json:poll{...}``
 * - Raw JSON with "json:poll string
 * - Raw JSON with pollId field
 */
export function parsePollFromContent(content: string): import('@/stores/discoveryAssistantStore').PollData | null {
  // Try standard format with newlines
  let pollMatch = content.match(/```json:poll\n([\s\S]+?)\n```/)
  if (pollMatch) {
    try {
      const pollData = JSON.parse(pollMatch[1])
      return pollData
    } catch (error) {
      console.error('Failed to parse poll JSON (format 1):', error)
    }
  }

  // Try format without newlines: ```json:poll{...}```
  pollMatch = content.match(/```json:poll\s*(\{[\s\S]+?\})\s*```/)
  if (pollMatch) {
    try {
      const pollData = JSON.parse(pollMatch[1])
      return pollData
    } catch (error) {
      console.error('Failed to parse poll JSON (format 2):', error)
    }
  }

  // Try two backticks: ``json:poll
  pollMatch = content.match(/``json:poll\s*(\{[\s\S]+?\})\s*``/)
  if (pollMatch) {
    try {
      const pollData = JSON.parse(pollMatch[1])
      return pollData
    } catch (error) {
      console.error('Failed to parse poll JSON (format 3):', error)
    }
  }

  // Try raw JSON starting with "json:poll or `json:poll
  pollMatch = content.match(/["`]json:poll["`]\s*:\s*(\{[\s\S]+?\})/)
  if (pollMatch) {
    try {
      const pollData = JSON.parse(pollMatch[1])
      return pollData
    } catch (error) {
      console.error('Failed to parse poll JSON (format 4):', error)
    }
  }

  // Try to find any JSON object with pollId field
  if (content.includes('pollId') && content.includes('"type"')) {
    // Extract JSON object
    const jsonMatch = content.match(/\{[^{}]*"pollId"[^{}]*\{[^{}]*\}[^{}]*\}|\{[^{}]*"pollId"[^{}]*\}/s)
    if (jsonMatch) {
      try {
        const pollData = JSON.parse(jsonMatch[0])
        if (pollData.pollId && pollData.type && pollData.options) {
          return pollData
        }
      } catch (error) {
        console.error('Failed to parse poll JSON (format 5):', error)
      }
    }
  }

  return null
}

/**
 * Parse multi-step question data from message content
 * Looks for ```json:multi-step code blocks and extracts multi-step configuration
 */
export function parseMultiStepFromContent(content: string): import('@/stores/discoveryAssistantStore').MultiStepQuestionData | null {
  const match = content.match(/```json:multi-step\n([\s\S]+?)\n```/)
  if (!match) return null

  try {
    const multiStepData = JSON.parse(match[1])
    return multiStepData
  } catch (error) {
    console.error('Failed to parse multi-step JSON:', error)
    return null
  }
}

/**
 * Strip poll/multi-step JSON blocks from content for display
 * Returns the text content without the JSON blocks
 */
export function stripInteractiveBlocks(content: string): string {
  return content
    .replace(/```json:poll\n[\s\S]+?\n```/g, '')
    .replace(/```json:multi-step\n[\s\S]+?\n```/g, '')
    .trim()
}
